import React, { useEffect, useState } from 'react';
import { getCurrentUser } from '../services/authService';
import { getUserReceipts, Receipt, saveReceipt } from '../services/dbService';
import { generatePdfReceipt } from '../services/receiptService';

// Helper function to format dates safely
const formatDate = (dateValue: any): string => {
  if (!dateValue) return 'N/A';
  
  try {
    // If it's a Firebase timestamp
    if (dateValue && typeof dateValue.toDate === 'function') {
      return dateValue.toDate().toLocaleDateString();
    }
    
    // If it's already a Date object
    if (dateValue instanceof Date) {
      return dateValue.toLocaleDateString();
    }
    
    // If it's a string or number, try to convert it
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) {
      return 'N/A';
    }
    return date.toLocaleDateString();
  } catch (err) {
    console.error('Error formatting date:', err);
    return 'N/A';
  }
};

// Safely extract amount from receipt
const safeGetAmount = (receipt: Receipt): number => {
  if (receipt.amount === undefined || receipt.amount === null) {
    return 0;
  }
  
  // Make sure it's a number
  const amount = typeof receipt.amount === 'number' 
    ? receipt.amount 
    : parseFloat(receipt.amount as unknown as string);
    
  // Return 0 if it's not a valid number
  return isNaN(amount) ? 0 : amount;
};

const Receipts: React.FC = () => {
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [editingReceipt, setEditingReceipt] = useState<string | null>(null);
  const [editAmount, setEditAmount] = useState<number>(0);
  const [debugInfo, setDebugInfo] = useState<string>('');
  
  useEffect(() => {
    let isMounted = true;
    
    const fetchReceipts = async () => {
      try {
        setDebugInfo('Checking for current user...');
        const user = getCurrentUser();
        
        if (!user) {
          console.error('No authenticated user found');
          setDebugInfo('No authenticated user found');
          if (isMounted) {
            setError('You must be logged in to view this page. Please log in again.');
            setLoading(false);
            setReceipts([]);
          }
          return;
        }
        
        setDebugInfo(`User found: ${user.uid}. Fetching receipts...`);
        console.log('Fetching receipts for user:', user.uid);
        
        const userReceipts = await getUserReceipts(user.uid);
        console.log('Receipts fetched:', userReceipts.length);
        setDebugInfo(`Fetched ${userReceipts.length} receipts`);
        
        // Ensure all receipts have basic required properties to avoid rendering issues
        const validatedReceipts = userReceipts.map(receipt => ({
          ...receipt,
          id: receipt.id || `temp-${Math.random().toString(36).substring(2, 9)}`,
          date: receipt.date || new Date(),
          bolNumber: receipt.bolNumber || 'Unknown',
          carrierName: receipt.carrierName || 'Unknown',
          amount: receipt.amount !== undefined && receipt.amount !== null ? receipt.amount : 0,
          status: receipt.status || 'pending'
        }));
        
        if (isMounted) {
          setReceipts(validatedReceipts);
          setLoading(false);
        }
      } catch (err: any) {
        console.error('Error fetching receipts:', err);
        setDebugInfo(`Error: ${err.message || 'Unknown error'}`);
        
        if (isMounted) {
          setError(err.message || 'Failed to load receipts');
          setLoading(false);
          // Set empty receipts array to prevent rendering issues
          setReceipts([]);
        }
      }
    };
    
    fetchReceipts();
    
    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, []);
  
  const handleStatusChange = async (receiptId: string, newStatus: 'pending' | 'paid' | 'cancelled') => {
    try {
      const receiptIndex = receipts.findIndex(r => r.id === receiptId);
      if (receiptIndex === -1) return;
      
      const updatedReceipt = { ...receipts[receiptIndex], status: newStatus };
      
      // Update in database
      await saveReceipt(updatedReceipt);
      
      // Update local state
      const updatedReceipts = [...receipts];
      updatedReceipts[receiptIndex] = updatedReceipt;
      setReceipts(updatedReceipts);
    } catch (err: any) {
      console.error('Error updating receipt status:', err);
      alert(`Failed to update receipt status: ${err.message}`);
    }
  };
  
  const startEditAmount = (receipt: Receipt) => {
    // Ensure we have an ID before setting it as the editing receipt
    if (receipt.id) {
      setEditingReceipt(receipt.id);
      setEditAmount(safeGetAmount(receipt));
    }
  };
  
  const cancelEditAmount = () => {
    setEditingReceipt(null);
    setEditAmount(0);
  };
  
  const saveEditAmount = async (receiptId: string) => {
    try {
      const receiptIndex = receipts.findIndex(r => r.id === receiptId);
      if (receiptIndex === -1) return;
      
      const updatedReceipt = { ...receipts[receiptIndex], amount: editAmount };
      
      // Update in database
      await saveReceipt(updatedReceipt);
      
      // Update local state
      const updatedReceipts = [...receipts];
      updatedReceipts[receiptIndex] = updatedReceipt;
      setReceipts(updatedReceipts);
      
      // Exit edit mode
      setEditingReceipt(null);
    } catch (err: any) {
      console.error('Error updating receipt amount:', err);
      alert(`Failed to update receipt amount: ${err.message}`);
    }
  };
  
  const downloadReceipt = async (receipt: Receipt) => {
    try {
      const pdfBytes = await generatePdfReceipt(receipt);
      
      // Create blob and download
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      // Use shipment number for filename if available, otherwise use BOL number
      const fileName = receipt.shipmentNumber 
        ? `Shipment_${receipt.shipmentNumber}.pdf`
        : `Receipt_${receipt.bolNumber || 'Unknown'}.pdf`;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error downloading receipt:', err);
      alert(`Failed to download receipt: ${err.message}`);
    }
  };
  
  // Filter receipts based on selected status
  const filteredReceipts = statusFilter === 'all' 
    ? receipts 
    : receipts.filter(r => r.status === statusFilter);
  
  // Sort receipts by date (newest first) with safer handling
  const sortedReceipts = [...filteredReceipts].sort((a, b) => {
    try {
      // Convert dates safely - check for Firebase timestamp first
      let dateA: Date;
      if (a.date && typeof a.date === 'object' && 'toDate' in a.date && typeof a.date.toDate === 'function') {
        dateA = a.date.toDate();
      } else if (a.date instanceof Date) {
        dateA = a.date;
      } else {
        dateA = new Date(a.date || 0);
      }
      
      let dateB: Date;
      if (b.date && typeof b.date === 'object' && 'toDate' in b.date && typeof b.date.toDate === 'function') {
        dateB = b.date.toDate();
      } else if (b.date instanceof Date) {
        dateB = b.date;
      } else {
        dateB = new Date(b.date || 0);
      }
      
      // Handle invalid dates by ensuring they don't cause sorting errors
      const timeA = !dateA || isNaN(dateA.getTime()) ? 0 : dateA.getTime();
      const timeB = !dateB || isNaN(dateB.getTime()) ? 0 : dateB.getTime();
      
      return timeB - timeA;
    } catch (error) {
      console.error('Error sorting dates:', error);
      return 0;
    }
  });
  
  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading">Loading receipts data...</div>
        <div className="debug-info">Debug: {debugInfo}</div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="dashboard-error">
        <div className="error-message">{error}</div>
        <div className="debug-info">Debug: {debugInfo}</div>
        <button 
          onClick={() => window.location.reload()} 
          className="retry-btn"
        >
          Retry
        </button>
      </div>
    );
  }
  
  return (
    <div className="receipts-page">
      <div className="page-header">
        <h1>NFI Lumper Receipt Creator | Receipts</h1>
        <div className="filters">
          <label htmlFor="status-filter">Filter by Status:</label>
          <select 
            id="status-filter" 
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="paid">Paid</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>
      
      {sortedReceipts.length === 0 ? (
        <p className="no-data">No receipts found.</p>
      ) : (
        <table className="receipts-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Shipment #</th>
              <th>BOL Number</th>
              <th>Carrier</th>
              <th>Origin</th>
              <th>Destination</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedReceipts.map(receipt => (
              <tr key={receipt.id}>
                <td>{formatDate(receipt.date)}</td>
                <td>{receipt.shipmentNumber || '-'}</td>
                <td>{receipt.bolNumber || 'Unknown'}</td>
                <td>{receipt.carrierName || 'Unknown'}</td>
                <td>{receipt.origin || '-'}</td>
                <td>{receipt.destination || '-'}</td>
                <td>
                  {editingReceipt === receipt.id ? (
                    <div className="edit-amount-container">
                      <input 
                        type="number"
                        value={editAmount}
                        onChange={(e) => setEditAmount(Number(e.target.value))}
                        min="0"
                        step="0.01"
                        className="edit-amount-input"
                        aria-label={`Edit amount for receipt ${receipt.bolNumber}`}
                      />
                      <div className="edit-amount-actions">
                        <button 
                          onClick={() => receipt.id && saveEditAmount(receipt.id)}
                          className="edit-amount-save"
                          title="Save"
                        >
                          ✓
                        </button>
                        <button 
                          onClick={cancelEditAmount}
                          className="edit-amount-cancel"
                          title="Cancel"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="amount-display">
                      ${safeGetAmount(receipt).toFixed(2)}
                      <button 
                        onClick={() => startEditAmount(receipt)}
                        className="edit-amount-btn"
                        title="Edit Amount"
                      >
                        ✎
                      </button>
                    </div>
                  )}
                </td>
                <td>
                  <select 
                    value={receipt.status || 'pending'}
                    onChange={(e) => receipt.id && handleStatusChange(
                      receipt.id, 
                      e.target.value as 'pending' | 'paid' | 'cancelled'
                    )}
                    className={`status-select status-${receipt.status || 'pending'}`}
                    aria-label={`Change status for receipt ${receipt.bolNumber}`}
                  >
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </td>
                <td>
                  <button 
                    className="action-btn download-btn"
                    onClick={() => downloadReceipt(receipt)}
                  >
                    Download
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default Receipts; 